<template>
  <div class="album-images-grid" ref="galleryRef">
    <div class="masonry-grid">
      <div
        v-for="(image, index) in images"
        :key="image.id || index"
        class="masonry-item"
      >
        <div
          class="image-card"
          :class="{ 'dropdown-active': activeDropdown === index }"
          @click="closeDropdown()"
        >
          <!-- Image -->
          <a
            :href="image.url || image.image_url || image"
            class="lightgallery-item"
            :data-src="image.image || image"
            :data-sub-html="image.username ? `<h4>${image.username}</h4>` : ''"
          >
            <img
              :src="image.thumb || image"
              :alt="`Album image ${index + 1}`"
              class="image-preview"
            />
          </a>

          <!-- Overlay -->
          <div class="image-overlay">
            <div class="image-info">
              <span class="username" v-if="image.username">
                <i class="fas fa-user mr-2"></i> {{ image.username }}
              </span>
            </div>

            <!-- Custom dropdown button -->
            <div class="image-actions" @click.stop>
              <button
                class="btn-action"
                @click.stop="toggleDropdown(index)"
              >
                <i class="fas fa-ellipsis-v"></i>
              </button>

              <transition name="fade">
                <div
                  v-if="activeDropdown === index"
                  class="dropdown-panel"
                >
                  <button @click="viewImage(image)">
                    <i class="fas fa-eye mr-2"></i> View
                  </button>
                  <div class="download-options">
                    <div class="download-options__label">
                      <i class="fas fa-download mr-2"></i> Download
                    </div>
                    <div class="download-options__buttons">
                      <button
                        v-for="option in downloadOptions"
                        :key="option.key"
                        @click="requestDownload(image, option.key)"
                      >
                        {{ option.label }}
                        <small v-if="option.hint">{{ option.hint }}</small>
                      </button>
                    </div>
                  </div>
                  <button
                    v-if="isHost"
                    class="text-danger"
                    @click="deleteImage(image)"
                  >
                    <i class="fas fa-trash mr-2"></i> Delete
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, nextTick, onBeforeUnmount } from 'vue'
import Swal from 'sweetalert2'
import api from '@/services/api'

// LightGallery imports
import lightGallery from 'lightgallery'
import lgZoom from 'lightgallery/plugins/zoom'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-zoom.css'
import 'lightgallery/css/lg-thumbnail.css'

const props = defineProps({
  images: { type: Array, default: () => [] },
  isHost: { type: Boolean, default: false },
  eventid: { type: [String, Number], required: true },
  userid: { type: [String, Number], required: true }
})

const emit = defineEmits(['image-deleted', 'image-download'])

const activeDropdown = ref(null)
const galleryRef = ref(null)
let galleryInstance = null
const downloadOptions = [
  { key: 'low', label: 'Low', hint: '≤800px' },
  { key: 'medium', label: 'Medium', hint: '≤1600px' },
  { key: 'high', label: 'High', hint: 'Original' }
]

// --- Toast helper ---
const toast = (msg, icon = 'success') =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title: msg,
    timer: 2500,
    showConfirmButton: false
  })

// --- Dropdown handling ---
const toggleDropdown = (index) => {
  activeDropdown.value = activeDropdown.value === index ? null : index
}
const closeDropdown = () => {
  activeDropdown.value = null
}

// --- LightGallery for single view ---
const viewImage = (image) => {
  closeDropdown()

  // Safe destroy of previous instance
  try {
    if (galleryInstance && galleryInstance.lgOpened) {
      galleryInstance.destroy(true)
      galleryInstance = null
    }
  } catch (err) {
    console.warn('LightGallery destroy skipped:', err)
  }

  // Create a new LightGallery instance dynamically
  galleryInstance = lightGallery(galleryRef.value, {
    dynamic: true,
    dynamicEl: [
      {
        src: image.image|| image,
        thumb: image.thumb || image,
        subHtml: image.username ? `<h4>${image.username}</h4>` : ''
      }
    ],
    plugins: [lgZoom, lgThumbnail],
    download: false
  })

  galleryInstance.openGallery()
}


// --- Download request (actual download handled by parent) ---
const requestDownload = (image, preset = 'high') => {
  closeDropdown()
  console.log(image);
  const sourceUrl = image.image || image.url || image.image_url || image.thumb || image
  if (!sourceUrl) {
    toast('Missing image source', 'error')
    return
  }

  const baseName =
    image.filename ||
    image.original_name ||
    `event-image-${props.eventid}-${Date.now()}`

  emit('image-download', {
    url: sourceUrl,
    name: baseName,
    preset,
    meta: image
  })

  toast('Preparing download...')
}

// --- Delete ---
const deleteImage = async (image) => {
  closeDropdown()
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: 'This image will be permanently deleted!',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  })

  if (result.isConfirmed) {
    try {
      const formData = new FormData()
      formData.append('file_path', image.photo_delete)
      formData.append('event_id', props.eventid)
      formData.append('user_id', props.userid)

      const response = await api.deleteAlbumImages(formData)
      if (response?.data?.status === 'ok') {
        toast('Image deleted successfully')
        emit('image-deleted')
      } else {
        toast('Failed to delete image', 'error')
      }
    } catch (error) {
      console.error('Delete error:', error)
      toast('Failed to delete image', 'error')
    }
  }
}

onMounted(async () => {
  await nextTick()
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.image-actions')) {
      closeDropdown()
    }
  })

  // Initialize LightGallery for all items
  galleryInstance = lightGallery(galleryRef.value, {
    selector: '.lightgallery-item',
    plugins: [lgZoom, lgThumbnail],
    download: true,
  })
})

onBeforeUnmount(() => {
  try {
    if (galleryInstance) {
      galleryInstance.destroy(true)
      galleryInstance = null
    }
  } catch (err) {
    console.warn('LightGallery cleanup skipped:', err)
  }
})
</script>
 <style scoped>
.album-images-grid {
  width: 100%;
  position: relative;
  z-index: 0;
}

.masonry-grid {
  column-count: 4;
  column-gap: 1rem;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 1rem;
  width: 100%;
  display: inline-block;
  position: relative;
}

.image-card {
  position: relative;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  background: #fff;
  z-index: 1;
  overflow: visible;
}

.image-card.dropdown-active {
  z-index: 30;
}

.image-card.dropdown-active .image-overlay {
  opacity: 1;
}

.image-card:hover {
  transform: translateY(-2px);
}

.image-preview {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: 10px;
  overflow: hidden;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 50%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2; /* overlay above image */
}
.image-card:hover .image-overlay { opacity: 1; }

.username {
  background: rgba(0,0,0,0.6);
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  font-size:10px;
}

.image-actions {
  position: relative;
  display: flex;
  justify-content: flex-end;
  z-index: 5; /* ensures button stays above overlay */
}

.btn-action {
  background: rgba(255,255,255,0.9);
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
  z-index: 6;
}
.btn-action:hover {
  background: white;
}

/* The custom dropdown panel */
.dropdown-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  min-width: 150px;
  z-index: 99999; /* << stays above everything */
}

.dropdown-panel button {
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  padding: 8px 14px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
}
.dropdown-panel button:hover {
  background-color: #f5f5f5;
}
.dropdown-panel .text-danger {
  color: #d33;
}

.download-options {
  padding: 6px 14px 10px;
  border-top: 1px solid rgba(0,0,0,0.05);
}
.download-options__label {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  font-weight: 600;
}
.download-options__buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.download-options__buttons button {
  flex: 1 1 30%;
  width: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
  border-radius: 999px;
  padding: 6px 10px;
  text-align: center;
  font-size: 12px;
  border: none;
  color: #333;
  transition: background 0.2s ease, color 0.2s ease;
}
.download-options__buttons button small {
  display: block;
  font-size: 10px;
  color: #777;
  margin-top: 2px;
}
.download-options__buttons button:hover {
  background: #e1e1e1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Masonry */
@media (max-width: 1200px) {
  .masonry-grid { column-count: 3; }
}
@media (max-width: 768px) {
  .masonry-grid { column-count: 2; }
}
@media (max-width: 480px) {
  .masonry-grid { column-count: 2; }
}
</style>
